import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Clock, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import BookingModal from "@/components/BookingModal";
import { waLink } from "@/lib/site";

export default function PackageDetail() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    setPkg(null);
    setNotFound(false);
    api.get(`/packages/${slug}`).then((r) => setPkg(r.data)).catch(() => setNotFound(true));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center">
        <h1 className="font-serif text-3xl font-semibold">Package not found</h1>
        <Link to="/" data-testid="package-not-found-home" className="mt-6 rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0F1418]">
          Back to all packages
        </Link>
      </div>
    );
  }

  if (!pkg) {
    return <div className="flex min-h-screen items-center justify-center text-white/40">Loading…</div>;
  }

  return (
    <>
      <Seo
        title={`${pkg.name} (${pkg.duration}) — Jay Jagannath Tour & Travels`}
        description={`${pkg.name}: ${pkg.points.join(", ")}. ${pkg.duration} chauffeur-driven tour from Rayagada, Odisha. Book on WhatsApp or online.`}
        path={`/packages/${pkg.slug}`}
      />
      <Navbar lang="EN" setLang={() => {}} onBook={() => setBookingOpen(true)} absoluteLinks />
      <main>
        <section className="relative flex min-h-[60vh] items-end overflow-hidden">
          <img src={pkg.image} alt={pkg.alt || pkg.name} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1418] via-[#0F1418]/50 to-[#0F1418]/40" />
          <div className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-14 pt-40 sm:px-8">
            <Link to="/#packages" data-testid="package-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-[#D4AF37]">
              <ArrowLeft className="h-4 w-4" /> All packages
            </Link>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
              <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.3em] text-[#D4AF37]">
                <Clock className="h-3.5 w-3.5" /> {pkg.duration}
              </p>
              <h1 className="mt-4 font-serif text-3xl font-bold tracking-tight sm:text-5xl">{pkg.name}</h1>
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-serif text-2xl font-semibold sm:text-3xl">Day by day</h2>
              <div className="mt-8 space-y-0">
                {pkg.itinerary.map((d, i) => (
                  <motion.div
                    key={d.day}
                    data-testid={`itinerary-day-${d.day}`}
                    initial={{ opacity: 0, x: -24 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="relative border-l border-[#D4AF37]/25 pb-10 pl-8 last:pb-0"
                  >
                    <span className="absolute -left-[9px] top-0 flex h-[18px] w-[18px] items-center justify-center rounded-full border border-[#D4AF37] bg-[#0F1418]" />
                    <p className="text-xs font-medium uppercase tracking-[0.25em] text-[#D4AF37]">Day {d.day}</p>
                    <h3 className="mt-2 font-serif text-xl font-semibold">{d.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">{d.detail}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <aside className="lg:sticky lg:top-28 lg:self-start">
              <div className="rounded-2xl border border-white/10 bg-[#171F26] p-7">
                <h2 className="font-serif text-xl font-semibold">What's included</h2>
                <ul className="mt-4 space-y-2.5">
                  {pkg.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-2.5 text-sm text-white/65">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" /> {inc}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid="package-book-btn"
                  onClick={() => setBookingOpen(true)}
                  className="group mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold text-[#0F1418] transition-transform hover:scale-[1.03]"
                >
                  Book Your Trip <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href={waLink(`Hello Jay Jagannath Tour & Travels, I want to book the "${pkg.name}" package (${pkg.duration}). Please share details.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="package-whatsapp-btn"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm font-medium text-white/85 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]"
                >
                  <MessageCircle className="h-4 w-4" /> Ask on WhatsApp
                </a>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer onBook={() => setBookingOpen(true)} />
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} vehicles={vehicles} packages={[pkg]} prefillDestination={pkg.name} />
    </>
  );
}
